// Supplier information
export interface SupplierInfo {
  id: number;
  name: string;
  status?: string;
}

export interface InventoryItem {
  id: string;
  originalId?: number;
  name: string;
  type: string;
  createdAt: string;
  recordCode: string | null;
  availabilityStatus: string;
  status: string;
  /** Raw API placement status (UNASSIGNED | ASSIGNED | IN_TRANSIT). */
  originalStatus?: string;
  updatedAt: string;
  location: string;
  material: string;
  price: number;
  locationName: string;
  /** Numeric ID of the current location; use for equality checks instead of `locationName`. */
  locationId?: number;
  /** Whether the piece has been transferred to a branch (API `transferredToBranch`). */
  transferredToBranch?: boolean;
  /** Location type from API (`location.type`). */
  locationType?: 'WAREHOUSE' | 'BRANCH';
  tags?: string[] | Array<{ name: string; color?: string; id?: number }>;
  firstAssignedAt?: string;
  /** Send / envío date (Fecha Transfer.). */
  transferredAt?: string;
  /** Branch reception date (stored; not always shown). */
  receivedAt?: string;
  transfer?: {
    id: number;
    transferCode: string;
    createdAt: string;
    status: string;
    completedAt?: string | null;
    cancelledAt?: string | null;
  };
  // Warehouse-specific fields
  condition?: string;
  itemNotes?: string;
  propertiesDescription?: string;
  weightGrams?: number;
  lengthMm?: number;
  diameterSize?: string;
  color?: string;
  workshopCode?: string;
  costPrice?: number;
  // Supplier information
  supplier?: SupplierInfo;
  // Product information for modal display
  product?: {
    id: number;
    name: string;
    code: string;
    description?: string;
    price?: string;
    imagePath?: string;
    thumbnailUrl?: string | null;
    categoryId?: number;
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface FiltersState {
  productCode: string;
  productName: string;
  productType: string;
  dateFrom: string;
  dateTo: string;
  recordNumber: string;
  status: string;
  tag: string;
}

// New filter state interface based on the API parameters
export interface InventoryFilterState {
  searchTerm: string;
  createdAtStart: string;
  createdAtEnd: string;
  status: string;
  locationId: string;
  categoryName?: string; // Tipo de producto
  recordCode?: string; // Número de registro
  searchTag?: string; // Etiqueta
  materialId?: string; // Material ID (exact match)
  diameterSize?: string; // Talla — filtro exacto (API: diameterSize)
  color?: string; // Color (partial match)
  workshopCode?: string; // Workshop code (partial match)
  supplierId?: string; // Proveedor de la pieza (exact match)
  /** Nombre del proveedor seleccionado (solo para el chip de filtro activo) */
  supplierName?: string;
  /** Inventario general: UNASSIGNED | ASSIGNED | IN_TRANSIT — API `status` */
  warehousePlacementStatus?: string;
  /** Inventario general: AVAILABLE | SOLD | … — API `availabilityStatus` */
  branchAvailabilityStatus?: string;
  weightGramsMin?: number;
  weightGramsMax?: number;
  salePriceMin?: number;
  salePriceMax?: number;
  salePricePerGramMin?: number;
  salePricePerGramMax?: number;
}

/** Server `sortBy` whitelist for warehouse inventory list (see backend `warehouse-inventory-list-sort`). */
export type WarehouseInventorySortField =
  | 'productCodeUnique'
  | 'productName'
  | 'categoryName'
  | 'createdAt'
  | 'recordCompletedAt'
  | 'recordCode'
  | 'status'
  | 'firstAssignedAt'
  | 'availabilityStatus'
  | 'locationName'
  | 'salePrice'
  | 'updatedAt';

export const DEFAULT_WAREHOUSE_INVENTORY_SORT: {
  sortBy: WarehouseInventorySortField;
  sortOrder: 'asc' | 'desc';
} = {
  sortBy: 'recordCompletedAt',
  sortOrder: 'desc',
};

/** Gestión en Sucursal: orden por fecha de ingreso (transferencia). */
export const DEFAULT_BRANCH_INVENTORY_SORT: {
  sortBy: WarehouseInventorySortField;
  sortOrder: 'asc' | 'desc';
} = {
  sortBy: 'firstAssignedAt',
  sortOrder: 'desc',
};

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
}

export interface InventoryStats {
  totalProducts: number;
  totalValue: number;
  productTypes: number;
  unassignedProducts: number;
  assignedProducts: number;
}
